// @flow

import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import ReactCodeInput from '../../utils/react-code-input';
import { useForm, Controller } from 'react-hook-form';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import StyledPhoneInput from '../SharedComponents/StyledPhoneInput';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material/styles';

import useTimer from '../../hooks/useTimer';
import useOnboardingRedirect from '../../hooks/useOnboardingRedirect';
import { getNextOnboardingStep } from '../../utils/onboardingUtils';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../../reduxStore/modules/user/selectors';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { fetchUserProfile } from '../../reduxStore/modules/user/actions';
import useAuthenticatedBackend from '../../hooks/useAuthenticatedBackend';
import AppLoadingSpinner from '../SharedComponents/AppLoadingSpinner';
import ContentContainer from '../SharedComponents/ContentContainer';
import { log, formatPhoneNumberForFrontend, formatPhoneNumberForBackend } from '../../utils/jsUtils';
import adTracking from '../../services/adTracking';
import BasicHeader from '../Billing/BasicHeader';
import { createClasses } from '../../utils/stylesUtils';

import verifyPhoneHomeStyles from './Styles/VerifyPhoneHome.Style';

type Props = {
  // Add your prop types here
};

const VerifyPhoneHome = (props: Props) => {
  const theme = useTheme();
  const classes = createClasses(verifyPhoneHomeStyles, theme);
  const history = useHistory();
  const dispatch = useDispatch();
  const loggedUser = useSelector(selectUser);
  const backend = useAuthenticatedBackend();

  // Redirect if user shouldn't be on this page (e.g., already verified)
  useOnboardingRedirect();


  const { handleSubmit, setValue, control, watch, formState: { errors } } = useForm({
    shouldUnregister: false,
    defaultValues: {
      phone: ''
    },
    mode: 'onChange'
  });

  const [registrationError, setRegistrationError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [userPhone, setUserPhone] = useState('');
  const [displayPhone, setDisplayPhone] = useState(''); // Phone number to show in readonly view
  const [isVerifyCodeLoading, setIsVerifyCodeLoading] = useState(false);
  const [isReadyToSendNewCode, setIsReadyToSendNewCode] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [codeValues, setCodeValues] = useState(['', '', '', '']);
  const [sendNewCodeTimer, setSendNewCodeTimer] = useState(0);
  const [timeLeftBeforeReadyToSendAgain, setTimeLeftBeforeReadyToSendAgain] = useState(0);

  const formEl = useRef();
  const autoSendTriggered = useRef(false);

  const [timeLeft, setTimeLeft] = useTimer((ticker) => {
    setTimeLeftBeforeReadyToSendAgain(ticker);
  }, () => {
    setIsReadyToSendNewCode(true);
  }, sendNewCodeTimer);
  
  useEffect(() => {
    if (loggedUser) {
      const formattedPhone = formatPhoneNumberForFrontend(loggedUser.Phone) || loggedUser.Phone || '';
      setUserPhone(formattedPhone);
      setValue('phone', formattedPhone);

      // Auto-send verification code when phone is prefilled from registration
      if (loggedUser.Phone && !autoSendTriggered.current) {
        autoSendTriggered.current = true;
        setDisplayPhone(formattedPhone);
        setCodeValues(['', '', '', '']);
        setSendingCode(true);
        setSendNewCodeTimer(60);
        setIsReadyToSendNewCode(false);
        const p = formatPhoneNumberForBackend(formattedPhone) || formattedPhone;
        backend.sendCodeToVerifyNb(p)
          .then(() => {
            setIsCodeSent(true);
            setSendingCode(false);
          })
          .catch(() => {
            setSendingCode(false);
          });
      }
    }
  }, [loggedUser, setValue, backend]);

  const onFieldChange = () => {
    setRegistrationError('');
  };

  const onCodeChange = (c) => {
    log('[onCodeChange] c: ', c);
    const values = Array(4).fill('');
    const split = c.split('');
    split.forEach((item, i) => {
      const cursor = i;
      if (cursor < 4) {
        values[cursor] = item;
      }
    });
    setCodeValues(values);

  };

  const handleVerifyPhone = (registration) => {
    setSendingCode(true);
    setSendNewCodeTimer(60);
    setIsReadyToSendNewCode(false);
    const phoneToVerify = typeof registration.phone !== 'undefined' ? registration.phone : userPhone;
    const p = formatPhoneNumberForBackend(phoneToVerify) || phoneToVerify;
    if (p !== null && p !== undefined && p !== '') {
      setUserPhone(phoneToVerify);
      setDisplayPhone(phoneToVerify); // Store the phone number for display
      setCodeValues(['', '', '', '']);
      log('[handleVerifyPhone] formatPhoneNumberForBackend(phone): ', p);
      log('[handleVerifyPhone] formatPhoneNumberIntl(p): ', formatPhoneNumberIntl(phoneToVerify));
      backend.sendCodeToVerifyNb(p)
        .then((response) => {
          log('[handleVerifyPhone] response -: ', response.data);
          log('[handleVerifyPhone] setting isCodeSent to true, displayPhone:', phoneToVerify);
          setIsCodeSent(true);
          setSendingCode(false);
        })
        .catch((err) => {
          setSendingCode(false);
          log('[handleCaptcha] verifyCaptcha error -: ', err);
        });
      }
  };

  const onCodeComplete = (code) => {
    log('[onCodeComplete] code: ', code);
    setIsVerifyCodeLoading(true);

    // Defensive validation: check if phone number is valid before API call
    const formattedPhone = formatPhoneNumberForBackend(userPhone);
    if (!formattedPhone) {
      log('[onCodeComplete] Invalid phone number:', userPhone);
      setIsVerifyCodeLoading(false);
      dispatch(setSnackMessage({
        message: 'Invalid phone number. Please edit and re-enter your phone number.',
        level: 'error',
        duration: 5000
      }));
      setIsCodeSent(false); // Allow user to edit phone number
      return;
    }

    // TODO: set timer and show error if code isn't verified after 10s.
    backend.verifyPhoneNbCode(formattedPhone, code)
      .then((response) => {
        log('[verifyPhoneNbCode] response: ', response);
        // set error msg in state to display
        // redirect to next step?
        // update store to show that user phone is now verified
        const {
          user,
          success,
          message
        } = response.data;
        setIsVerifyCodeLoading(false);
        dispatch(setSnackMessage({
          message: message,
          level: success === 'success' ? 'success' : 'error',
          duration: 5000
        }));
        if (success && user) {
          // Fetch updated user profile to refresh all user data in Redux
          dispatch(fetchUserProfile(user.Uid))
            .then((profileResult) => {
              // After successful phone verification, redirect to next onboarding step
              adTracking.trackEvent('CompleteRegistration', { content_name: 'phone_verified' });
              adTracking.trackGoogleConversion('CompleteRegistration');
              const nextStep = getNextOnboardingStep('/verifyPhone');
              log('[onCodeComplete] Phone verified successfully, redirecting to', nextStep);
              history.push(nextStep);
            })
            .catch((error) => {
              log('[onCodeComplete] Error fetching profile after verification:', error);
              // Even if profile fetch fails, redirect since phone verification was successful
              const nextStep = getNextOnboardingStep('/verifyPhone');
              log('[onCodeComplete] Phone verified, redirecting to', nextStep, 'despite profile error');
              history.push(nextStep);
            });
        }
      })
      .catch((err) => {
        log('[handleCaptcha] verifyCaptcha error -: ', err);
        // set error msg in state to display
        setIsVerifyCodeLoading(false);
        dispatch(setSnackMessage({
          message: err.message,
          level: 'error',
          duration: 5000
        }));
      });
  };

  return (
    <Box sx={classes.container}>
      <Helmet>
        <title>Neo | Verify Phone</title>
      </Helmet>
      <BasicHeader title="Verify your phone" />
      <AppLoadingSpinner />
      <ContentContainer hasSubheader={false}>
        <form
          onSubmit={handleSubmit(handleVerifyPhone)}
          ref={formEl}
          style={{width: '100%'}}
        >
          {
            registrationError !== ''
            ? <Typography
                sx={classes.errorMessage}
                variant={'body1'}
              >{registrationError}</Typography>
            : null
          }
        <Paper
          sx={classes.formPaper}
          elevation={2}
          square
        >
          <Box>
            <Typography variant='h3'>
              Verify your personal number
            </Typography>
            <Typography variant='body1'>
              We will send a verification code to your phone number
            </Typography>
            {
              isCodeSent
              ? <Box sx={classes.readonlyPhoneContainer}>
                <Typography
                  sx={classes.readonlyPhone}
                  variant='h4'
                >
                  {(() => {
                    const formattedPhone = formatPhoneNumberIntl(displayPhone);
                    // Defensive fallback: use displayPhone if formatPhoneNumberIntl returns undefined
                    return formattedPhone || displayPhone || '';
                  })()}
                  <IconButton
                    aria-label='Search'
                    sx={classes.editButton}
                    onClick={e => {
                      e.stopPropagation();
                      setIsCodeSent(false);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Typography>
              </Box>
              : <>
                  <Controller
                    control={control}
                    name='phone'
                    defaultValue=''
                    rules={{
                      required: 'Phone number is required',
                      validate: (value) => {
                        const formatted = formatPhoneNumberForBackend(value);
                        if (!formatted) {
                          return 'Please enter a valid phone number';
                        }
                        return true;
                      }
                    }}
                    render={({ onChange, value, onBlur }) => (
                      <Box sx={classes.phoneInputContainer}>
                        <StyledPhoneInput
                          value={value || ''}
                          onChange={(newValue) => {
                            onChange(newValue);
                            setUserPhone(newValue || '');
                            onFieldChange();
                          }}
                          onBlur={onBlur}
                          placeholder='Your phone number'
                          defaultCountry='US'
                          variant='standard'
                          disabled={!!loggedUser?.Phone}
                        />
                      </Box>
                    )}
                  />
                  {errors.phone && (
                    <Typography
                      sx={{ color: 'error.main', mt: 1, fontSize: '0.875rem' }}
                      variant='body2'
                    >
                      {errors.phone.message || 'Phone number is required'}
                    </Typography>
                  )}
                </>
            }
            
            {
              !isCodeSent
              ? <Button
                  sx={classes.submitButton}
                  color='primary'
                  disabled={sendingCode}
                  type='submit'
                  variant='contained'
                >
                  {sendingCode ? 'Sending Code...' : 'Send Code'}
              </Button>
              : null
            }

            {
              isCodeSent
              ? <>
                <Box sx={classes.codeInputContainer}>
                  <ReactCodeInput
                    sx={classes.codeInput}
                    fields={4}
                    loading={isVerifyCodeLoading}
                    onChange={onCodeChange}
                    onComplete={onCodeComplete}
                    type='number'
                    values={codeValues}
                  />
                </Box>
                <a alt='resend'
                  sx={!isReadyToSendNewCode ? classes.resendLinkDisabled : classes.resendLink}
                  href='#'
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    isReadyToSendNewCode && handleSubmit(handleVerifyPhone)();
                  }}
                  type='submit'
                >
                  <Typography variant='body1'>Resend Code{ timeLeftBeforeReadyToSendAgain > 0 ? ` ( ${timeLeftBeforeReadyToSendAgain} )` : ''}</Typography>
                </a>
              </>
              : null
            }  

          </Box>
        </Paper>
        </form>
      </ContentContainer>
    </Box>
  );
};

export default VerifyPhoneHome;